import type { MaybeRef } from 'vue';
import type { SurpriseBag } from '@pkg/types/surpriseBag';
import { getTitle } from '@pkg/utilities/surprisebag';
import { surpriseBagSeoKeyword } from '../../../sanity/schemas/utils/slugs/slugConstants';
import { useBreadcrumbJsonLd } from './useBreadcrumbJsonLd';
import {
  absolutePageUrlFromPath,
  useJsonLdShared,
  type BreadcrumbCrumb,
} from './useJsonLdShared';

/**
 * Store/bag page: Home > Find Food > [Store].
 * City is omitted — /find/[city] 404s and Google only allows a label-only ListItem as the last node.
 */
export function useSurpriseBagBreadcrumbJsonLd(
  bag: MaybeRef<SurpriseBag | null | undefined>,
  findLabel?: MaybeRef<string | null | undefined>,
) {
  const { t } = useI18n();
  const route = useRoute();
  const { localePath, baseUrl } = useJsonLdShared();

  const pageUrl = computed(() => {
    if (!unref(bag)) {
      return null;
    }
    return absolutePageUrlFromPath(baseUrl.value, route.path);
  });

  const crumbs = computed((): BreadcrumbCrumb[] | null => {
    const bagData = unref(bag);
    const url = pageUrl.value;
    if (!bagData || !url) {
      return null;
    }

    const storeName = (
      getTitle(bagData.store?.storeName, bagData.store?.branch) ||
      bagData.displayName ||
      ''
    ).trim();
    if (!storeName) {
      return null;
    }

    const homepageUrl = `${baseUrl.value}${localePath('/')}`;
    const findHomeUrl = `${baseUrl.value}${localePath(`/${surpriseBagSeoKeyword}`)}`;
    const sectionLabel = (unref(findLabel) || 'Find Food').trim() || 'Find Food';

    return [
      { name: t('home'), item: homepageUrl },
      { name: sectionLabel, item: findHomeUrl },
      { name: storeName, item: url },
    ];
  });

  useBreadcrumbJsonLd(crumbs, pageUrl, 'surprise-bag-breadcrumb-jsonld');
}
